import React, { ChangeEvent, Component, ReactNode } from 'react';
import { PushNotification } from '../../../../domain/notification/NotificationDomains';
import { Link } from 'react-router-dom';
import ComponentUtil from '../../../../util/ComponentUtil';
import Icon from '../../../../icons/Icon';
import { CheckerRequest, CheckerRequestType } from '../../../../domain/maker-checker/MakerCheckerDomains';
import MakerCheckerClient from '../../../../client/maker-checker/MakerCheckerClient';
import GatewayContainer from '../../../../config/GatewayContainer';
import GatewayNames from '../../../../constant/GatewayNames';
import LanguageUtil from '../../../../util/LanguageUtil';
import DeployErrorUtil from '../../../../util/DeployErrorUtil';
import ComponentName from '../../../../constant/ComponentName';

class NotificationCheckerRequestComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly makerCheckerClient: MakerCheckerClient = GatewayContainer.getInstance().get(GatewayNames.MAKER_CHECKER_CLIENT);

  public render(): ReactNode {
    const { comment } = this.state;
    const { notification } = this.props;
    const canMakeAction: boolean =
      !(notification.metadata?.readonly ?? false) && (notification.metadata.status == 'Waiting' || notification.metadata.status == undefined);
    return (
      <div className='flex gap-x-3 w-full'>
        <div>
          <span className='flex justify-center items-center shrink-0 w-10 h-10 rounded-[50%] bg-[#10C0DD] text-white'>
            {notification.metadata.username?.substring(0, 1)?.toUpperCase()}
          </span>
        </div>
        <div className='flex w-full flex-col gap-y-3'>
          <div className='flex flex-col gap-y-1 w-full'>
            <div className='flex justify-between w-full'>
              <div className='text-f-black-2'>{this.getOperationType()}</div>
              <div className='flex gap-x-2'>
                <span className='text-[#607080] text-2xs'>{this.getTimeDifference()}</span>
                <span className={`${this.getStatusColor()} mt-1 border rounded-[50%] flex items-center justify-center text-center w-2 h-2`}></span>
              </div>
            </div>
            <div>
              <span className='flex gap-x-1 flex-wrap items-center text-f-black-2'>
                <span className='text-[#458EF7] text-sm'>@{notification.metadata.username}</span>
                <span>{this.getMessage()}</span>
                {notification.metadata.component === ComponentName.LIST_DATA ? (
                  <>
                    <span className='font-medium'>{notification.metadata.itemName}</span>
                    <span>{LanguageUtil.get({ id: 'common.label.itemOn', defaultMessage: 'item on' })}</span>
                    <Link to={this.makeUrl()} className='text-[#458EF7] text-sm'>
                      {notification.metadata.name}
                    </Link>
                  </>
                ) : (
                  <Link to={this.makeUrl()} className='text-[#458EF7] text-sm'>
                    {notification.metadata.documentId}
                  </Link>
                )}
              </span>
            </div>
          </div>
          <div className='relative'>
            <textarea
              disabled={notification.metadata.comment != undefined || !canMakeAction}
              className='focus-textarea-shadow text-[#607080] resize-none w-full border border-[#E0E4E8] rounded-[4px] py-3 pl-3 pr-6 hover:border-[#728EFF] focus:outline-[#728EFF]'
              placeholder='Comment...'
              value={notification.metadata.comment || comment}
              onChange={this.onChangeComment}
            ></textarea>
            <span className='absolute right-2 top-2 text-[#607080]'>
              <Icon icon='edit' size={20} />
            </span>
          </div>
          <div className='flex pb-1'>{this.getOperations()}</div>
          {canMakeAction && (
            <div className='flex flex-row-reverse gap-x-2'>
              <span className='bg-main-gray text-sm rounded-[500px] cursor-pointer text-[#50A67F] py-2 px-3' onClick={this.approve}>
                {LanguageUtil.get({ id: 'common.label.approve', defaultMessage: 'Approve' })}
              </span>
              <span className='bg-main-gray text-sm rounded-[500px] cursor-pointer text-[#D2332F] py-2 px-3' onClick={this.reject}>
                {LanguageUtil.get({ id: 'common.label.decline' })}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  private makeUrl = () => {
    const { notification } = this.props;
    try {
      const { component, documentId } = notification.metadata;
      return ComponentUtil.handleLink(component, documentId, notification.metadata) || '#';
    } catch (e) {
      return '#';
    }
  };

  private getMessage = () => {
    const { notification } = this.props;
    switch (notification.metadata.operationType as CheckerRequestType) {
      case 'DEPLOY':
        return 'sent deploy request for';
      case 'UNDEPLOY':
        return 'sent undeploy request for';
      case 'DELETE':
        return 'sent delete request for';
      case 'UPDATE':
        return 'sent update request for';
      case 'SAVE':
        return 'sent save request for';
    }
  };

  private getOperationType = () => {
    const { notification } = this.props;
    switch (notification.metadata.operationType as CheckerRequestType) {
      case 'DEPLOY':
        return 'Deployment Request';
      case 'UNDEPLOY':
        return 'Undeployment Request';
      case 'DELETE':
        return 'Delete Request';
      case 'SAVE':
        return 'Save Request';
      case 'UPDATE':
        return 'Update Request';
    }
  };

  private getTimeDifference = () => {
    const { notification } = this.props;
    const timeDifference = new Date().getTime() - new Date(notification.timestamp).getTime();
    const isSeconds = timeDifference < 60 * 1000;
    const isMinutes = timeDifference < 60 * 60 * 1000 && timeDifference > 60 * 1000;
    const isHours = timeDifference < 24 * 60 * 60 * 1000 && timeDifference > 60 * 60 * 1000;
    const isDays = timeDifference < 7 * 24 * 60 * 60 * 1000 && timeDifference > 24 * 60 * 60 * 1000;
    const isWeeks = timeDifference < 30 * 7 * 24 * 60 * 60 * 1000 && timeDifference > 7 * 24 * 60 * 60 * 1000;
    const isMonths = timeDifference < 12 * 30 * 7 * 24 * 60 * 60 * 1000 && timeDifference > 30 * 7 * 24 * 60 * 60 * 1000;
    const isYears = timeDifference > 12 * 30 * 7 * 24 * 60 * 60 * 1000;
    if (isSeconds) {
      return 'Just now';
    }
    if (isMinutes) {
      return Math.floor(timeDifference / 60000) + ' minutes ago';
    }
    if (isHours) {
      return Math.floor(timeDifference / 3600000) + ' hours ago';
    }
    if (isDays) {
      return Math.floor(timeDifference / 86400000) + ' days ago';
    }
    if (isWeeks) {
      return Math.floor(timeDifference / 604800000) + ' weeks ago';
    }
    if (isMonths) {
      return Math.floor(timeDifference / 2628000000) + ' months ago';
    }
    if (isYears) {
      return Math.floor(timeDifference / 31540000000) + ' years ago';
    }
  };

  private onChangeComment = (e: ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ comment: e.target.value });
  };

  private approve = async () => {
    const { comment } = this.state;
    const { notification } = this.props;
    try {
      await this.makerCheckerClient.approve(notification.metadata.id, comment);
    } catch (error: unknown) {
      if (DeployErrorUtil.show(error, LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' }))) {
        return;
      }
      throw error;
    }
  };

  private reject = async () => {
    const { comment } = this.state;
    const { notification } = this.props;
    await this.makerCheckerClient.reject(notification.metadata.id, comment);
  };

  private getStatusColor = () => {
    const { notification } = this.props;
    const status = notification.metadata.status;
    switch (status) {
      case 'Approve':
        return 'bg-[#50A67F]';
      case 'Reject':
        return 'bg-[#D2332F]';
      case 'Waiting':
      default:
        return 'bg-[#458EF7]';
    }
  };

  private getOperations = () => {
    const { request } = this.props;
    if (request == undefined) return null;
    const approved = request.actions.filter((action) => action.type == 'Approve');
    const rejected = request.actions.filter((action) => action.type == 'Reject');
    switch (request.status) {
      case 'Waiting':
        return <span className='text-f-black-2'>{LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' })}</span>;
      case 'Approve':
        return (
          <div className='text-f-black-2 flex gap-0.5'>
            {LanguageUtil.get({ id: 'common.label.approvedBy', defaultMessage: 'Approved by:' })}
            <span className='text-[#458EF7]'>{approved.map((action) => `@${action.username}`).join(', ')}</span>
          </div>
        );
      case 'Reject':
        return (
          <div className='text-f-black-2 flex gap-0.5'>
            {LanguageUtil.get({ id: 'common.label.rejectBy', defaultMessage: 'Reject by:' })}
            <span className='text-[#458EF7]'>{rejected.map((action) => `@${action.username}`).join(', ')}</span>
          </div>
        );
      default:
        return null;
    }
  };
}

class State {
  comment: string = '';
}

interface Props {
  notification: PushNotification;
  request: CheckerRequest | undefined;
}

export default NotificationCheckerRequestComponent;
